import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { message } from 'antd'
import { useState } from 'react'
import { EstimateStatuses } from '../../../common/constants'
import DraggableJobKanbanCard from '../../JobKanban/DraggableJobKanbanCard'
import JobKanbanCard from '../../JobKanban/JobKanbanCard'
import { useSetEstimateLostMutation } from '../../JobKanban/jobKanbanApi'
import {
  columnDroppableId, effectiveStatusOf, PendingMove, removePendingMove, resolveDrop
} from '../../JobKanban/jobKanbanDragModel'
import { compactDollars, estimateColumns, KanbanColumn, labelFor } from '../../JobKanban/jobKanbanModel'
import { JobListItem } from '../../JobList/jobListItem'
import { EstimatesSummary } from './estimatesReportApi'
import { useEstimatesReportInvalidator } from './estimatesReportQueries'
import { cardsForColumn, columnTakesTheCard, columnTotal, defaultEstimatesBoardSort, sortCards } from './estimatesKanbanModel'
import { BoardSortColumn } from '../../JobList/jobListFiltersModel'
import '../../JobKanban/JobKanbanBoard.css'

type Props = {
  estimates: EstimatesSummary
  condensed?: boolean
  sortColumn?: BoardSortColumn
  sortDescending?: boolean
}

const DRAG_ACTIVATION_DISTANCE = 8

type ColumnProps = {
  column: KanbanColumn
  cards: JobListItem[]
  dragging: boolean
  takesTheCard: boolean
  condensed?: boolean
}

const Column = ({ column, cards, dragging, takesTheCard, condensed }: ColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: columnDroppableId(column.status),
    data: { status: column.status }
    /* Deliberately not disabled — see JobKanbanColumn. A disabled droppable is invisible to
       dnd-kit, which would leave a refused drop with nothing to explain. */
  })

  const classNames = ['job-kanban__column']
  if (isOver && takesTheCard) {
    classNames.push('job-kanban__column--over')
  }
  if (dragging && takesTheCard) {
    classNames.push('job-kanban__column--open')
  }
  if (dragging && !takesTheCard) {
    classNames.push('job-kanban__column--closed')
  }

  return <div ref={setNodeRef} className={classNames.join(' ')} role="listitem" aria-label={column.label}>
    <header className="job-kanban__column-header" style={{ borderBlockStartColor: column.accent }}>
      <div className="job-kanban__column-title">
        <span className="job-kanban__dot" style={{ backgroundColor: column.accent }} />
        <span className="job-kanban__column-label">{column.label}</span>
        <span className="job-kanban__count">{cards.length}</span>
      </div>
      <div className="job-kanban__column-total">{compactDollars(columnTotal(cards))}</div>
    </header>

    <div className="job-kanban__column-body">
      {cards.length === 0 && <p className="job-kanban__empty">Nothing here</p>}
      {cards.map(card => <DraggableJobKanbanCard
        key={card.id}
        job={card}
        accent={column.accent}
        from={column.status}
        condensed={condensed}
      />)}
    </div>

    {dragging && !takesTheCard && column.closedReason && <div className="job-kanban__column-veil">
      <span className="job-kanban__column-veil-text">{column.closedReason}</span>
    </div>}
  </div>
}

const EstimatesKanbanBoard = ({
  estimates,
  condensed,
  sortColumn = defaultEstimatesBoardSort.column,
  sortDescending = defaultEstimatesBoardSort.descending
}: Props) => {
  const [pendingMoves, setPendingMoves] = useState<PendingMove[]>([])
  const [draggingJob, setDraggingJob] = useState<JobListItem | undefined>()
  const report = useEstimatesReportInvalidator()

  const dropPendingMove = (jobId: number) => setPendingMoves(moves => removePendingMove(moves, jobId))

  const setEstimateLost = useSetEstimateLostMutation({
    onFailure: (jobId, failure) => {
      dropPendingMove(jobId)
      message.error(failure)
    }
  })

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE }
  }))

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggingJob(undefined)

    const resolution = resolveDrop(
      estimateColumns,
      false,
      pendingMoves,
      { job: active.data.current?.job, from: active.data.current?.from },
      { status: over?.data.current?.status }
    )

    if (!resolution.moved) {
      if (resolution.refusedReason) {
        message.warning(`${resolution.job!.name} cannot move to ${labelFor(estimateColumns, resolution.to!)} — ${resolution.refusedReason.toLowerCase()}.`)
      }
      return
    }

    setPendingMoves(resolution.moves)
    const job = resolution.job!

    if (resolution.undone) {
      message.info(`${job.name} put back in ${labelFor(estimateColumns, resolution.to!)}.`)
      return
    }

    setEstimateLost.mutate(
      { jobId: job.id, lost: resolution.to === EstimateStatuses.Lost },
      {
        /* The report is one query rather than one per column, so the move can be released as
           soon as it has been refetched — there is no per-column source to watch. */
        onSuccess: async () => {
          await report.invalidate()
          dropPendingMove(job.id)
        }
      }
    )
  }

  const draggingStatus = draggingJob ? effectiveStatusOf(draggingJob, false, pendingMoves) : undefined
  const draggingAccent = estimateColumns.find(column => column.status === draggingStatus)?.accent

  return <DndContext
    sensors={sensors}
    onDragStart={({ active }: DragStartEvent) => setDraggingJob(active.data.current?.job as JobListItem | undefined)}
    onDragEnd={onDragEnd}
    onDragCancel={() => setDraggingJob(undefined)}
  >
    <div className="job-kanban" role="list" aria-label="Pipeline columns">
      {estimateColumns.map(column => <Column
        key={column.status}
        column={column}
        cards={sortCards(cardsForColumn(estimates, pendingMoves, column.status), sortColumn, sortDescending)}
        dragging={!!draggingJob}
        takesTheCard={columnTakesTheCard(column, draggingStatus)}
        condensed={condensed}
      />)}
    </div>

    <DragOverlay dropAnimation={null} zIndex={9999}>
      {draggingJob && <JobKanbanCard
        job={draggingJob}
        accent={draggingAccent ?? 'transparent'}
        overlay
        condensed={condensed}
      />}
    </DragOverlay>
  </DndContext>
}

export default EstimatesKanbanBoard
